import { useCallback, useMemo, type ChangeEventHandler } from "react";

import { FormRowField, FormikForm, LandingPageFieldText } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { moduleSchema } from "@skilitics/nautilus-endpoint-definition";

import { FormBody, Header, Heading, PageContainer, StyledForm, SubHeading } from "../../shared";
import { useCreateModuleSettings, type CreateModuleSettings } from "../CreateModuleSettingsContext";

type FormValues = Pick<CreateModuleSettings["metadata"], "title" | "learningObjective"> &
  Pick<CreateModuleSettings, "utilizeAi">;

// const DropdownsContainer = styled.div`
//   display: flex;
//   align-items: flex-start;
//   gap: 26px;
// `;

// const SelectMenuProps = {
//   PaperProps: {
//     style: {
//       maxHeight: "300px",
//     },
//   },
// };

const validationSchema = moduleSchema.pick({ title: true, learningObjective: true });

export const ModuleDetailsPage = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const { metadata, updateMetadata, utilizeAi, setUtilizeAi } = useCreateModuleSettings();

  const initialValues: FormValues = useMemo(
    () => ({
      title: metadata.title,
      learningObjective: metadata.learningObjective,
      utilizeAi,
    }),
    [metadata.learningObjective, metadata.title, utilizeAi],
  );

  const handleSubmit = useCallback(
    ({ utilizeAi: useAi, ...rest }: FormValues) => {
      updateMetadata(rest);
      setUtilizeAi(useAi);
    },
    [setUtilizeAi, updateMetadata],
  );

  // hack because the add button won't light up until onBlur for some reason even if turn on validateOnChange
  // but I can't see how on earth it takes validation into account anyway
  const handleTitleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (e) => {
      updateMetadata({ title: e.currentTarget.value });
    },
    [updateMetadata],
  );

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
        <SubHeading>{formatMessage("subHeading")}</SubHeading>
      </Header>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formatStrings}
        validationSchema={validationSchema}
        dataTest="module-details-form"
      >
        <StyledForm blurOnEnter={false}>
          <FormBody>
            <FormRowField<FormValues> name="title">
              <LandingPageFieldText onChange={handleTitleChange} autoFocus />
            </FormRowField>

            <FormRowField<FormValues> name="learningObjective">
              <LandingPageFieldText multiline rows={4} />
            </FormRowField>

            {/* TODO: What are we doing with language for MVP? */}
            {/* <DropdownsContainer>
              <FormRowField<FormValues> name="defaultLanguage">
                <FieldSelect
                  options={languages.map(({ code, title: label }) => ({ label, value: code }))}
                  MenuProps={SelectMenuProps}
                />
              </FormRowField>
            </DropdownsContainer>

            <FormRowField<FormValues> name="utilizeAi">
              <HelpContainer>
                <FieldSwitch label={<SwitchLabel>{formatMessage("utilizeAiLabel")}</SwitchLabel>} />
                <HelpTooltip title={formatMessage("utilizeAiDescription")} />
              </HelpContainer>
            </FormRowField> */}
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "New Training Module",
  },
  subHeading: {
    en: "Module Details",
  },
  titleLabel: {
    en: "What is the title of your module?",
  },
  titleDescription: {
    en: `A title which will be shown on the module card in the Projects navigation area. A pipe ("|") character may be used to create a folder, such as "folder | sub-folder | title".`,
  },
  titlePlaceholder: {
    en: "Add a title...",
  },
  learningObjectiveLabel: {
    en: "What is the module's primary learning objective?",
  },
  learningObjectiveDescription: {
    en: "A short, concise outline of the module's principal learning objective.",
  },
  learningObjectivePlaceholder: {
    en: "Add a learning objective...",
  },
  defaultLanguageLabel: {
    en: "Default Language",
  },
  utilizeAiLabel: {
    en: "Utilize an AI learning assistant on this project",
  },
  utilizeAiDescription: {
    en: "Use an AI learning assistant to aid with module creation.",
  },
} satisfies FormatMessages;
